import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CustomerHeader from "@/components/CustomerHeader";
import { Sparkles, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";

const SPECIALTY_OPTIONS = ["Kahve", "Tarot", "El", "Rüya", "Katina", "İskambil", "Aşk", "Burç", "Pirinç"];

export default function FalciBasvuru() {
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    display_name: "",
    bio: "",
    specialties: [],
    experience_years: 5,
    sample_reading: "",
    price_per_reading: 100,
    phone: "",
  });

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({ ...f, full_name: user.name, display_name: user.name }));
    api.get("/falci/apply/mine").then(({ data }) => { if (data && data.id) setExisting(data); }).catch(() => {});
  }, [user]);

  const toggleSpec = (s) => {
    setForm((f) => ({ ...f, specialties: f.specialties.includes(s) ? f.specialties.filter((x) => x !== s) : [...f.specialties, s] }));
  };

  const submit = async () => {
    if (!form.display_name || !form.bio || form.specialties.length === 0) {
      toast.error("Lütfen görünen ad, biyografi ve en az bir uzmanlık gir");
      return;
    }
    setBusy(true);
    try {
      await api.post("/falci/apply", form);
      toast.success("Başvurun alındı! Admin onayı sonrası falcı olarak görüneceksin.");
      const { data } = await api.get("/falci/apply/mine");
      setExisting(data);
      await refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || "Başvuru başarısız"); }
    finally { setBusy(false); }
  };

  if (user?.role === "falci") {
    return (
      <div className="cosmic-bg min-h-screen">
        <div className="stars" />
        <CustomerHeader />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Sparkles className="w-16 h-16 gold-text mx-auto mb-4" />
          <h1 className="font-serif-myst text-4xl mb-2">Zaten Falcısın ✨</h1>
          <p className="text-white/60 mb-6">Falcı paneline giderek konsültasyonlarını yönetebilirsin.</p>
          <button onClick={() => nav("/falci-panel")} className="btn-gold px-6 py-3 rounded-full">Falcı Paneline Git</button>
        </main>
      </div>
    );
  }

  return (
    <div className="cosmic-bg min-h-screen">
      <div className="stars" />
      <CustomerHeader />
      <main className="max-w-2xl mx-auto px-4 py-8" data-testid="falci-apply-page">
        <h1 className="font-serif-myst text-4xl md:text-5xl mb-2">Fal Bakmak İster misin?</h1>
        <p className="text-white/60 mb-6">FalZilla platformunda gerçek falcı olarak kullanıcılara yorum yap, kazanç elde et. Admin onayından sonra profilin yayına girer.</p>

        {existing && existing.status === "pending" && (
          <div className="glass rounded-2xl p-5 border border-amber-400/40 mb-6 flex items-start gap-3" data-testid="existing-pending">
            <Clock className="w-6 h-6 text-amber-300 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-200">Başvurun inceleniyor</div>
              <div className="text-sm text-white/60 mt-1">Admin onayı bekleniyor. Onaylandığında bildirim alacaksın.</div>
            </div>
          </div>
        )}
        {existing && existing.status === "rejected" && (
          <div className="glass rounded-2xl p-5 border border-red-400/40 mb-6 flex items-start gap-3" data-testid="existing-rejected">
            <XCircle className="w-6 h-6 text-red-300 mt-0.5" />
            <div>
              <div className="font-semibold text-red-200">Başvurun reddedildi</div>
              <div className="text-sm text-white/60 mt-1">{existing.admin_notes || "Detaylar için destek ile iletişime geç."}</div>
              <button onClick={() => setExisting(null)} className="text-xs gold-text underline mt-2">Yeniden başvur</button>
            </div>
          </div>
        )}

        {(!existing || existing.status === "rejected") && (
          <div className="glass rounded-3xl p-6 space-y-4">
            <Field label="Görünen Ad *"><input data-testid="apply-display-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="input" /></Field>
            <Field label="Tam Ad Soyad"><input data-testid="apply-full-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="input" /></Field>
            <Field label="Telefon (opsiyonel)"><input data-testid="apply-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" /></Field>
            <Field label="Biyografi *"><textarea data-testid="apply-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} className="input" placeholder="Kendini tanıt, deneyimlerini anlat..." /></Field>
            <Field label="Uzmanlık Alanları *">
              <div className="flex flex-wrap gap-2">
                {SPECIALTY_OPTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => toggleSpec(s)} data-testid={`spec-${s.toLowerCase()}`} className={`px-3 py-1.5 rounded-full text-xs transition-colors ${form.specialties.includes(s) ? "btn-gold" : "bg-white/5 text-white/70"}`}>{s}</button>
                ))}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Deneyim (yıl)"><input data-testid="apply-experience" type="number" min={0} value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value) || 0 })} className="input" /></Field>
              <Field label="Fiyat (jeton / yorum)"><input data-testid="apply-price" type="number" min={10} value={form.price_per_reading} onChange={(e) => setForm({ ...form, price_per_reading: parseInt(e.target.value) || 100 })} className="input" /></Field>
            </div>
            <Field label="Örnek Yorum (bir kahve/tarot yorumu yaz)">
              <textarea data-testid="apply-sample" value={form.sample_reading} onChange={(e) => setForm({ ...form, sample_reading: e.target.value })} rows={5} className="input" placeholder="Kullanıcılara örnek olması için kısa bir yorum..." />
            </Field>
            <button onClick={submit} disabled={busy} data-testid="apply-submit" className="btn-gold w-full py-3 rounded-full flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Başvuruyu Gönder
            </button>
            <p className="text-[11px] text-white/40 text-center">Onaylanma süreci 24-48 saat sürebilir.</p>
          </div>
        )}
      </main>
      <style>{`.input{width:100%;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);border-radius:12px;padding:10px 14px;color:white;font-size:14px}.input:focus{outline:none;border-color:#D4AF37}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs text-white/60 mb-1 block">{label}</label>{children}</div>;
}
